import React, { useState, useEffect } from "react";
import { Modal, Typography, Button, ButtonGroup, Grid, Box, CircularProgress, Rating } from "@mui/material";
import { Movie as MovieIcon, Theaters, Language, PlusOne, Favorite, FavoriteBorderOutlined, Remove, ArrowBack } from "@mui/icons-material";

import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { useGetMovieQuery, useGetRecommendationsQuery, useGetListQuery } from "../../services/TMDB";
import { selectGenreOrCategory } from '../../features/currentGenreOrCategory';
import { userSelector } from '../../features/auth';
import { MovieList } from "..";

import useStyles from "./styles";
import genreIcons from '../../assets/genres';


const MovieInformation = () => {
  const { user } = useSelector(userSelector);
  const { id } = useParams(); //* Movie ID
  const navigate = useNavigate();
  const { data, isFetching, error } = useGetMovieQuery(id); //* Movie Data
  const [open, setOpen] = useState(false);

  const { data: recommendations } =
    useGetRecommendationsQuery({ movie_id: id, list: '/recommendations' }); //* Movie Recommendations Data
  const { data: favoriteMovies } = useGetListQuery(
    { listName: 'favorite/movies', accountId: user.id, sessionId: localStorage.getItem('session_id'), page: 1 },
    { skip: !user.id },
  );
  const { data: watchlistMovies } = useGetListQuery(
    { listName: 'watchlist/movies', accountId: user.id, sessionId: localStorage.getItem('session_id'), page: 1 },
    { skip: !user.id },
  );
  const dispatch = useDispatch();

  const { classes } = useStyles(); //* styles hook
  const URL_PATH = `https://image.tmdb.org/t/p/w500/`;  //* tmdb url path
  const IMDB_URL = `https://www.imdb.com/title/`;  //* IMDB url path
  const YOUTUBE_URL = `https://www.youtube.com/embed/`; //* YouTube URL
  const ACTORS_COUNT = 6;

  const [isMovieFavorite, setIsMovieFavorite] = useState(false);
  const [isMovieWatchListed, setIsMovieWatchListed] = useState(false);

  useEffect(() => {
    setIsMovieFavorite(!!favoriteMovies?.results?.find((movie) => movie?.id === data?.id));
  }, [favoriteMovies, data]);

  useEffect(() => {
    setIsMovieWatchListed(!!watchlistMovies?.results?.find((movie) => movie?.id === data?.id));
  }, [watchlistMovies, data]);

  const addToFavorites = async () => {
    await axios.post(`https://api.themoviedb.org/3/account/${user.id}/favorite?api_key=${process.env.REACT_APP_TMDB_KEY}&session_id=${localStorage.getItem('session_id')}`, {
      media_type: 'movie',
      media_id: id,
      favorite: !isMovieFavorite,
    });

    setIsMovieFavorite((prev) => !prev);
  };

  const addToWatchList = async () => {
    await axios.post(`https://api.themoviedb.org/3/account/${user.id}/watchlist?api_key=${process.env.REACT_APP_TMDB_KEY}&session_id=${localStorage.getItem('session_id')}`, {
      media_type: 'movie',
      media_id: id,
      watchlist: !isMovieWatchListed,
    });

    setIsMovieWatchListed((prev) => !prev);
  };

  //* Loader
  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size="8rem" />
      </Box>
    )
  }

  // ! Error Handling
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Link to="/">Something has gone wrong - Go back</Link>
      </Box>
    )
  }

  return (
    <Grid container className={classes.containerSpaceAround}>
      {/* Movie Poster */}
      <Grid item sm={12} lg={4}>
        <img
          className={classes.poster}
          src={`${URL_PATH}${data?.poster_path}`}
          alt={data?.title}
        />
      </Grid>
      {/* End of Movie Poster Grid */}

      {/* Main Movie info grid */}
      <Grid item container direction="column" lg={7}>
        {/* Movie Title */}
        <Typography variant="h3" align="center" gutterBottom>
          {data?.title} ({data.release_date.split('-')[0]})
        </Typography>

        {/* Movie Tagline */}
        <Typography variant="h5" align="center" gutterBottom>
          {data?.tagline}
        </Typography>

        {/* Rating and Runtime Container */}
        <Grid container className={classes.containerSpaceAround}>
          {/* Movie Star Rating & Rating */}
          <Box display="flex" align="center">
            <Rating readOnly value={data.vote_average / 2} />
            <Typography variant="subtitle1" gutterBottom style={{ marginLeft: '10px' }}>
              {data?.vote_average.toFixed(1)} /10
            </Typography>
          </Box>

          {/* Movie Runtime & Languages */}
          <Typography variant="h6" align="center" gutterBottom >
            {data?.runtime}min {data?.spoken_languages.length > 0 ? `/ ${data?.spoken_languages[0].name}` : ''}
          </Typography>
        </Grid>
        {/* End of Rating and runtime container */}

        {/* Genres Container */}
        <Grid item className={classes.genresContainer}>
          {/* Looping Over genres */}
          {data?.genres?.map((genre, i) => (
            <Link key={genre.name} className={classes.links} to="/" onClick={() => dispatch(selectGenreOrCategory(genre.id))}>

              {/* Genre Icon */}
              <img src={genreIcons[genre.name.toLowerCase()]} alt='category-logo' className={classes.genreImage} height={30} />

              {/* Genre Name */}
              <Typography color="textPrimary" variant="subtitle1" >
                {genre?.name}
              </Typography>
            </Link>
          ))}

        </Grid>
        {/* End of Genres Container */}

        {/* Overview Heading */}
        <Typography variant="h5" gutterBottom style={{ marginTop: '10px' }}>
          Overview
        </Typography>

        {/* Movie Overview */}
        <Typography style={{ marginBottom: '2rem' }}>
          {data?.overview}
        </Typography>

        {/* Top Cast */}
        <Typography variant="h5" gutterBottom >
          Top Cast
        </Typography>

        {/* Cast Grid */}
        <Grid item container spacing={2}>
          {/* Only showing top 6 cast & cast with images */}
          {data && data.credits?.cast?.map((character, i) => (
            character.profile_path && (
              <Grid key={i} item xs={4} md={2}
                component={Link} to={`/actors/${character.id}`}
                style={{ textDecoration: 'none' }}>

                {/* Cast Image */}
                <img
                  className={classes.castImage}
                  src={`${URL_PATH}${character.profile_path}`}
                  alt={character.name}
                />

                {/* Cast Name */}
                <Typography color="textPrimary" >
                  {character?.name}
                </Typography>

                {/* Character Name */}
                <Typography color="textSecondary" >
                  {character?.character.split('/')[0]}
                </Typography>
              </Grid>
            )
          )).slice(0, ACTORS_COUNT)}

        </Grid>
        {/* End of Cast Grid */}

        {/* Button Grid */}
        <Grid item container style={{ marginTop: '2rem' }}>
          <div className={classes.buttonsContainer}>
            <Grid item xs={12} sm={6} className={classes.buttonsContainer}>
              <ButtonGroup size="medium" variant="outlined">
                {/* Website Button */}
                <Button target="_blank" rel="noopener noreferre" href={data?.homepage} endIcon={<Language />}>
                  Website
                </Button>

                {/*IMDB Button  */}
                <Button target="_blank" rel="noopener noreferre" href={`${IMDB_URL}${data?.imdb_id}`} endIcon={<MovieIcon />}>
                  IMDB
                </Button>

                {/*Trailer Button  */}
                <Button onClick={() => setOpen(true)} href='#' endIcon={<Theaters />}>
                  Trailer
                </Button>
              </ButtonGroup>
            </Grid>

            <Grid item xs={12} sm={6} className={classes.buttonsContainer}>
              <ButtonGroup size="medium" variant="outlined">
                {/* Favorite Button */}
                <Button onClick={addToFavorites} endIcon={isMovieFavorite ? <FavoriteBorderOutlined /> : <Favorite />}>
                  {isMovieFavorite ? 'Unfavorite' : 'Favorite'}
                </Button>

                {/* Watch list Button */}
                <Button onClick={addToWatchList} endIcon={isMovieWatchListed ? <Remove /> : <PlusOne />}>
                  Watchlist
                </Button>

                {/* Back Button */}
                <Button onClick={() => navigate(-1)} endIcon={<ArrowBack />} sx={{ borderColor: 'primary.main', textUnderLine: 'none' }}>
                  <Typography style={{ textDecoration: 'none' }} color="inherit" variant="subtitle2">
                    Back
                  </Typography>
                </Button>
              </ButtonGroup>
            </Grid>
          </div>

        </Grid>
        {/* End of Buttons Grid */}

      </Grid>
      {/*End of Main Movie info grid */}

      {/* Movie Recommendation Section */}
      <Box marginTop="5rem" width="100%">
        <Typography variant="h3" gutterBottom align="center">
          You might also like
        </Typography>

        {/* Loop Through recommended movies */}
        {recommendations
          ? <MovieList movies={recommendations} numberOfMovies={12} />
          : <Box>Sorry, Nothing was found.</Box>
        }

      </Box>

      {/* Movie Trailer Modal */}
      <Modal
        closeAfterTransition
        className={classes.modal}
        open={open}
        onClose={() => setOpen(false)}
      >
        {data?.videos?.results?.length > 0 && (
          <iframe
            autoPlay
            className={classes.video}
            title="Trailer"
            frameBorder="0"
            src={`${YOUTUBE_URL}${data.videos.results[0].key}`}
            allow="autoplay"
          />
        )}
      </Modal>
    </Grid>
  );
};

export default MovieInformation;
